import {
  LinearProgress as MuiLinearProgress,
  LinearProgressProps as MuiLinearProgressProps,
  Stack,
  styled,
  useThemeProps,
} from '@mui/material';
import clsx from 'clsx';
import React from 'react';
import { Typography } from 'src/components/Typography/Typography';
import generateGuid from 'src/utils/generate_guid';
import linearProgressClasses from './linearProgressClasses';
import { inProgressColor } from './inProgressColor';

declare module '@mui/material/LinearProgress' {
  interface LinearProgressPropsColorOverrides {
    /** Even though this is a new color, we'll never pass this color to the LinearProgress component.
     * See {@link inProgressColor} for more info. */
    inProgress: true;
  }
}

declare module '@mui/material/styles' {
  interface PaletteLinearProgress {
    inProgressBg: string;
  }
}

const LinearProgressLabel = styled(Typography, {
  name: 'MuiLinearProgress',
  slot: 'Label',
  overridesResolver: (styles) => styles.label,
})({});

export interface LinearProgressProps extends MuiLinearProgressProps {
  /**
   * Whether to display the progress value as a label.
   *
   * Only works when `variant` is `determinate`.
   */
  visibleValue?: boolean;
}

/**
 * Progress indicators inform customers about the duration and progression of ongoing processes.
 *
 * Demos:
 *
 * - [LinearProgress](https://unify.workiva.org/components/progress?tab=code#linear)
 *
 * API:
 *
 * - [LinearProgress API](https://unify.workiva.org/components/progress?tab=props#linearprogress)
 */
export const LinearProgress = React.forwardRef<
  HTMLSpanElement,
  LinearProgressProps
>(function LinearProgress(inProps, ref) {
  const props = useThemeProps({ props: inProps, name: 'MuiLinearProgress' });
  const {
    color: colorProp = 'primary',
    value = 0,
    variant = 'indeterminate',
    visibleValue = false,
    sx,
    className,
    ...rest
  } = props;

  const labelId = React.useRef(generateGuid(4, 'progress-label-')).current;
  const isDeterminate = variant === 'determinate';
  const showLabel = isDeterminate && visibleValue;

  const classes = clsx(
    showLabel && linearProgressClasses.visibleValue,
    className
  );

  let color: typeof colorProp = colorProp;
  if (colorProp === 'inProgress') {
    color = inProgressColor; // See inProgressColor for more info
  }

  // Base progress bar component that will be used in both cases
  const progressBar = (
    <MuiLinearProgress
      value={value}
      variant={variant}
      className={classes}
      color={color}
      aria-labelledby={showLabel ? labelId : undefined}
      {...rest}
      ref={ref}
      sx={{ width: '100%', ...sx }}
    />
  );

  // Return either the wrapped version with label or the base progress bar
  return showLabel ? (
    <Stack
      direction="row"
      alignItems="center"
      gap={1}
      className={linearProgressClasses.wrapper}
    >
      {progressBar}
      <LinearProgressLabel
        id={labelId}
        variant="body2"
        className={linearProgressClasses.label}
        aria-hidden
      >
        {`${Math.round(value)}%`}
      </LinearProgressLabel>
    </Stack>
  ) : (
    progressBar
  );
});
export default LinearProgress;
